import Vue from 'vue';
import Vuex from 'vuex';
import _ from 'lodash';
import moment from 'moment';
import axios from 'axios';
import { auth, db, fieldValue } from '../services/firebase';
import { db_helper } from './db_helper.js';

Vue.use(Vuex);

let Respondents = db.collection('respondents');
let Surveys = db.collection('studies');
let Users = db.collection('users');
let Misc = db.collection('misc');
let Accounts = db.collection('accounts');

const demo_survey_id = 'cNUZcoeTjtX1h9vUEama';

export const store = new Vuex.Store({

  strict: true,
  state: {

    user: {},
    account: {},

    surveys: null,
    respondents: {}, // Cached profile info for all our respondents.

    comments_answer_id: null, // Since comments don't have a route param to check

    new_survey: {
      emoji_icon: getRandomEmoji(),
      shortcode: getRandomShortcode(),
      language: "en",
      title: '',
      starting_message: {
        link: '',
        push: {
          id: '',
          vars: [],
          image: '',
          type: ''
        }
      },
      questions: [],
    },

    // Used in account settings
    team_members: [],

    // Used to ensure data isn't loaded twice for no reason; Also used to show loading states
    fetched_data: {},

    // For watching loading state
    loading_data: {
      is_sending_queued_pushes: false
    },

    billing: null,

  },

  mutations: {

    initUser(state, user) {
      state.user = user;
    },

    initAccount(state, account) {
      state.account = account;
      if (!account.push_templates) {
        state.account.push_templates = [
          {
            text: 'Hi, you requested to chat with Outside Voice using this phone number. Was this you?',
            id: '25a65fb1-bd8c-44a5-8fbd-64ceaf7e13d4',
            type: 'image'
          },
          {
            text: 'Hi __, how was your experience with __? Can you give us quick feedback?',
            id: '945fa588-2295-4c31-9a34-bdf8af7271c3',
            type: 'image'
          }
        ];
      };
    },

    initTeamMembers(state, team_members) {
      state.team_members = team_members;
    },

    setFetched(state, key) {
      state.fetched_data[key] = true;
    },

    // Both methods below are used to immediately update the user after authing, in order to refresh the UI ASAP
    updateUserName(state, name) {
      state.user.name = name;
    },

    updateUser(state, data) {
      if (data.name) {
        state.user.name = data.name;
      };
      if (data.first_name) {
        state.user.first_name = data.first_name;
      };
      if (data.last_name) {
        state.user.last_name = data.last_name;
      };
      if (data.profile_pic) {
        state.user.profile_pic = data.profile_pic;
      };
      if (data.role) {
        state.user.role = data.role;
      };
      if (data['settings.is_wallet_hidden'] !== undefined) {
        if (state.user.settings) {
          state.user.settings.is_wallet_hidden = data['settings.is_wallet_hidden'];
        } else {
          let copy = JSON.parse(JSON.stringify(state.user));
          copy.settings = { is_wallet_hidden: data['settings.is_wallet_hidden'] };
          state.user = copy;
        };
      };
    },

    initSurveys(state, surveys) {
      state.surveys = surveys;
    },
    updateSurveyIndexes(state) {
      _.forEach(state.surveys, (value, index) => {
        if (state.surveys[index]) {
          state.surveys[index].index = index;
        };
      });
    },

    setWhatsappPushes(state, { survey_index, whatsapp_pushes }) {
      let copy = JSON.parse(JSON.stringify(state.surveys)); // Deep copy which is needed to trigger changes in components.
      copy[survey_index].whatsapp_pushes = whatsapp_pushes;
      state.surveys = copy;
    },

    updateWhatsappPush(state, { survey_index, push }) {
      // Update the push with it's new info
      let copy = JSON.parse(JSON.stringify(state.surveys)); // Deep copy which is need to trigger changes in components.
      let push_index = _.findIndex(copy[survey_index].whatsapp_pushes, ['id', push.id]);
      if (push_index !== -1) {
        _.assign(copy[survey_index].whatsapp_pushes[push_index], push);
        state.surveys = copy;
      };
    },

    setWhatsappPushStatusSending(state, { survey_index, push_id }) {
      let copy = JSON.parse(JSON.stringify(state.surveys)); // Deep copy which is needed to trigger changes in components.
      let push_index = _.findIndex(copy[survey_index].whatsapp_pushes, ['id', push_id]);
      if (push_index !== -1) {
        copy[survey_index].whatsapp_pushes[push_index].status = 'sending';
        //copy[survey_index].whatsapp_pushes[push_index].time_sent = moment().unix();
      };
      state.surveys = copy;
    },

    removeWhatsappPushes(state, { survey_index, push_ids }) {
      let copy = JSON.parse(JSON.stringify(state.surveys)); // Deep copy which is needed to trigger changes in components.
      for (let push_id of push_ids) {
        copy[survey_index].whatsapp_pushes = _.filter(copy[survey_index].whatsapp_pushes, push => push.id !== push_id);
      };
      state.surveys = copy;
    },

    setAnswers(state, { survey_index, answers }) {
      let copy = JSON.parse(JSON.stringify(state.surveys)); // Deep copy which is needed to trigger changes in components.
      if (copy[survey_index].answers) {
        for (let answer of answers) {
          let is_answer_already_included = _.findIndex(copy[survey_index].answers, ['id', answer.id]);
          if (is_answer_already_included === -1) {
            copy[survey_index].answers.push(answer);
          };
        };
      } else {
        copy[survey_index].answers = answers;
      };
      state.surveys = copy;
    },

    addNewAnswer(state, { survey_index, answer }) {

      let copy = JSON.parse(JSON.stringify(state.surveys));

      // Add the answer to the array of answers, and increment the answer count for the particular question_index
      if (copy[survey_index].answers) {
        copy[survey_index].answers.unshift(answer);
      } else {
        copy[survey_index].answers = [answer];
      };
      if (copy[survey_index].questions[answer.question_index].answer_count !== undefined) {
        copy[survey_index].questions[answer.question_index].answer_count++;
      } else {
        copy[survey_index].questions[answer.question_index].answer_count = 1;
      };

      // First answer in the survey - add user to started_user in survey, which will update survey card
      if (answer.question_index === 0) {
        if (copy[survey_index].link_click_count !== undefined) { // NOTE: A bit hacky.
          copy[survey_index].link_click_count++;
        };
        if (copy[survey_index].started_users) {
          copy[survey_index].started_users.push(answer.uid);
        } else {
          copy[survey_index].started_users = [answer.uid];
        };
      };

      state.surveys = copy;
    },

    updateNewAnswer(state, { survey_index, answer }) {
      // Update the newly added answer (needed after porting file from UIB)
      let copy = JSON.parse(JSON.stringify(state.surveys)); // Deep copy which is need to trigger changes in components.
      let answer_index = _.findIndex(copy[survey_index].answers, ['id', answer.id]);
      copy[survey_index].answers[answer_index] = answer;
      state.surveys = copy;
    },

    setRecentAnswers(state, { survey_index, recent_answers }) {
      let copy = JSON.parse(JSON.stringify(state.surveys)); // Deep copy which is need to trigger changes in components.
      copy[survey_index].recent_answers = recent_answers.slice(0,10);
      state.surveys = copy;
    },

    addNewRecentAnswer(state, { survey_index, recent_answer }) {
      let copy = JSON.parse(JSON.stringify(state.surveys));
      // Add the recent answer to the array of recent answers
      if (copy[survey_index].recent_answers) {
        copy[survey_index].recent_answers.unshift(recent_answer);
      } else {
        copy[survey_index].recent_answers = [recent_answer];
      };
      copy[survey_index].recent_answers = copy[survey_index].recent_answers.slice(0,10);
      state.surveys = copy;
    },

    deleteAnswer(state, data) {
      let [survey_index, question_index, answer_id, respondent_id] = data;
      let copy = JSON.parse(JSON.stringify(state.surveys)); // Deep copy which is need to trigger changes in components.
      copy[survey_index].answers = _.filter(copy[survey_index].answers, answer => answer.id !== answer_id);
      copy[survey_index].questions[question_index].answer_count -= 1; // Decrement answer count
      if (respondent_id) {
        copy[survey_index].started_users = _.filter(copy[survey_index].started_users, uid => uid !== respondent_id); // Remove uid from started_users (used for # of responses)
      };
      state.surveys = copy;
    },

    deleteRecentAnswer(state, data) {
      let [survey_index, respondent_id] = data;
      let copy = JSON.parse(JSON.stringify(state.surveys)); // Deep copy which is need to trigger changes in components.
      copy[survey_index].recent_answers = _.filter(copy[survey_index].recent_answers, answer => answer.uid !== respondent_id);
      state.surveys = copy;
    },

    // Used in the user-info on answer cards
    setRespondent(state, respondent) {
      let copy = Object.assign({}, state.respondents);
      copy[respondent.uid] = respondent;
      state.respondents = copy;
    },

    setRespondentNPSScore(state, { uid, nps_score }) {
      let copy = Object.assign({}, state.respondents);
      copy[uid].nps_score = nps_score;
      state.respondents = copy;
    },

    setSurveyRespondents(state, respondents) {
      let copy = Object.assign({}, state.respondents);
      for (let respondent of respondents) {
        let nps_score = copy[respondent.uid] && copy[respondent.uid].nps_score !== undefined ? copy[respondent.uid].nps_score : '';
        if (nps_score !== '') {
          respondent.nps_score = nps_score;
        };
        copy[respondent.uid] = respondent;
      };
      state.respondents = copy;
    },

    // Opens up comments drawer
    setCommentsAnswerID(state, answer_id) {
      state.comments_answer_id = answer_id;
    },

    addComment(state, { survey_index, comment }) {
      let copy = JSON.parse(JSON.stringify(state.surveys)); // Deep copy which is need to trigger changes in components.
      let answer_index = _.findIndex(copy[survey_index].answers, ['id', state.comments_answer_id]);
      if (answer_index !== -1) { // Comment made on the answers page for a certain question
        if (copy[survey_index].answers[answer_index].comments) {
          copy[survey_index].answers[answer_index].comments = _.concat(copy[survey_index].answers[answer_index].comments, comment);
        } else {
          copy[survey_index].answers[answer_index].comments = [ comment ];
        };
        state.surveys = copy;
      };
    },

    toggleIsFavorited(state, { survey_index, answer_id, is_favorited }) {
      let copy = JSON.parse(JSON.stringify(state.surveys)); // Deep copy which is need to trigger changes in components.
      let answer_index = _.findIndex(copy[survey_index].answers, ['id', answer_id]);
      if (answer_index !== -1) { // Comment made on the answers page for a certain question
        copy[survey_index].answers[answer_index].is_favorited = is_favorited;
        state.surveys = copy;
      };
    },

    editAnswerTranscript(state, { survey_index, answer_id, edited_transcript }) {
      let copy = JSON.parse(JSON.stringify(state.surveys)); // Deep copy which is need to trigger changes in components.
      let answer_index = _.findIndex(copy[survey_index].answers, ['id', answer_id]);
      if (answer_index !== -1) {
        copy[survey_index].answers[answer_index].transcription = edited_transcript;
        state.surveys = copy;
      };
    },



    /********

    NEW SURVEY MUTATIONS

    *******/

    updateSurveyTitle(state, title) {
      state.new_survey.title = title;
    },
    updateSurveyLanguage(state, language) {
      state.new_survey.language = language;
    },
    updateSurveyEmoji(state, emoji) {
      state.new_survey.emoji_icon = emoji;
    },

    updateLinkStartingText(state, text) {
      state.new_survey.starting_message.link = text;
    },

    initStartingMessagePush(state, push_info) {
      state.new_survey.starting_message.push = push_info;
    },
    updateStartingMessagePushVar(state, { var_text, var_index }) {
      let copy = JSON.parse(JSON.stringify(state.new_survey)); // Deep copy which is need to trigger changes in components.
      copy.starting_message.push.vars[var_index] = var_text;
      state.new_survey = copy;
    },

    addStartingMessageImage(state, { image_url, is_local }) {
      let copy = JSON.parse(JSON.stringify(state.new_survey)); // Deep copy which is need to trigger changes in components.
      copy.starting_message.push.image = image_url;
      if (is_local) {
        copy.starting_message.push.is_uploading = true;
      } else {
        delete copy.starting_message.push.is_uploading;
      };
      state.new_survey = copy;
    },

    removeStartingMessageImage(state) {
      let copy = JSON.parse(JSON.stringify(state.new_survey)); // Deep copy which is need to trigger changes in components.
      copy.starting_message.push.image = '';
      delete copy.starting_message.push.is_uploading;
      state.new_survey = copy;
    },

    updateQuestionText(state, { question_index, text }) {
      getNewSurveyQuestions()[question_index].text = text;
    },

    addQuestionBlock(state, { answer_type, insert_at_index }) {

      let new_question = {
        text: '',
        answer_type,
        answer_options: {
          is_multipart: false,
          is_required: false,
          go_to: 'none'
        },
        media: [
          // {
          //   url, type
          // }
        ]
      };

      if (answer_type === 'image') {
        new_question.answer_options.is_image_intelligence = false;
      };


      if (answer_type === 'multi_choice') {
        new_question.multi_choice = [
          {
            text: '',
            go_to: 'none',
            answer_count: 0
          },
          // {
          //   text: '',
          //   go_to: 'none',
          //   answer_count: 0
          // }
        ];
        delete new_question.answer_options.is_multipart;
        delete new_question.answer_options.is_image_intelligence;
        delete new_question.answer_options.go_to;
        new_question.answer_options.is_multiselect = false;
      };

      if (answer_type === 'nps') {
        delete new_question.answer_options.is_multipart;
      };

      if (insert_at_index !== undefined) {
        state.new_survey.questions.splice(insert_at_index, 0, new_question);
      } else {
        state.new_survey.questions.push(new_question);
      };

      // Adjust any affected go_to indexes
      let added_at_index = insert_at_index !== undefined ? insert_at_index : state.new_survey.questions.length-1;
      _.forEach(state.new_survey.questions, (question, question_index) => {
        if (question.answer_options.go_to >= added_at_index) {
          question.answer_options.go_to++;
        };
        if (question.answer_type === 'multi_choice') {
          _.forEach(question.multi_choice, (option, option_index) => {
            if (option.go_to >= added_at_index) {
              option.go_to++;
            };
          });
        };
      });

    },


    updateQuestionAnswerType(state, { question_index, new_answer_type }) {

      let question = getNewSurveyQuestions()[question_index];
      let old_answer_type = question.answer_type;

      // Add and remove data required for 'multi_choice'
      if (new_answer_type === 'multi_choice') {

        question.multi_choice = [
          {
            text: '',
            go_to: 'none',
            answer_count: 0
          },
          // {
          //   text: '',
          //   go_to: 'none',
          //   answer_count: 0
          // }
        ];
        question.answer_options.is_multiselect = false;

        delete question.answer_options.is_multipart;
        delete question.answer_options.is_image_intelligence;
        delete question.answer_options.go_to;

      // Remove unncessary 'multi-choice' attributes, if previous answer was multi_choice
      } else if (old_answer_type === 'multi_choice') {

        if (question.answer_options.is_multipart === undefined) {
          question.answer_options.is_multipart = false;
        };
        if (question.answer_options.go_to === undefined) {
          question.answer_options.go_to = null;
        };
        if (question.answer_options.is_required === undefined) {
          question.answer_options.is_required = false;
        };

        delete question.multi_choice;
        delete question.answer_options.is_multiselect;

      };

      if (new_answer_type === 'image') {
        question.answer_options.is_image_intelligence = false;
      } else if (old_answer_type === 'image') {
        delete question.answer_options.is_image_intelligence
      };

      // Remove unncessary 'Say' attributes
      if (new_answer_type === 'say') {
        delete question.answer_options.is_multipart;
        delete question.answer_options.is_image_intelligence;
        delete question.answer_options.is_required;
      };

      if (new_answer_type === 'nps') {
        delete question.answer_options.is_multipart;
      };

      question.answer_type =  new_answer_type;

      getNewSurveyQuestions()[question_index] = question;

    },


    addOption(state, { question_index }) {
      getNewSurveyQuestions()[question_index].multi_choice.push({
        text: '',
        go_to: 'none',
        answer_count: 0
      });
    },
    removeOption(state, { question_index, option_index }) {
      getNewSurveyQuestions()[question_index].multi_choice.splice(option_index, 1);
    },
    updateOptionText(state, { question_index, option_index, text }) {
      let copy = JSON.parse(JSON.stringify(state.new_survey)); // Deep copy which is need to trigger changes in components.
      copy.questions[question_index].multi_choice[option_index].text = text;
      state.new_survey = copy;
    },
    updateOptionGoTo(state, { question_index, option_index, go_to_index }) {
      getNewSurveyQuestions()[question_index].multi_choice[option_index].go_to = go_to_index;
    },

    addQuestionMedia(state, { question_index, type, url }) {
      getNewSurveyQuestions()[question_index].media.push({ type, url, is_uploading: true });
    },

    setQuestionMedia(state, { question_index, url }) {
      let copy = JSON.parse(JSON.stringify(state.new_survey)); // Deep copy which is need to trigger changes in components.
      copy.questions[question_index].media[[getNewSurveyQuestions()[question_index].media.length - 1]].url = url;
      delete copy.questions[question_index].media[[getNewSurveyQuestions()[question_index].media.length - 1]].is_uploading;
      state.new_survey = copy;
    },
    removeQuestionMedia(state, { question_index, media_index }) {
      getNewSurveyQuestions()[question_index].media.splice(media_index, 1);
    },

    updateQuestionGoTo(state, { question_index, go_to_index }) {
      getNewSurveyQuestions()[question_index].answer_options.go_to = go_to_index;
    },

    toggleRequired(state, { index }) {
      getNewSurveyQuestions()[index].answer_options.is_required = !getNewSurveyQuestions()[index].answer_options.is_required;
    },
    toggleMultipart(state, { index }) {
      getNewSurveyQuestions()[index].answer_options.is_multipart = !getNewSurveyQuestions()[index].answer_options.is_multipart;
    },
    toggleMultiselect(state, { index }) {
      getNewSurveyQuestions()[index].answer_options.is_multiselect = !getNewSurveyQuestions()[index].answer_options.is_multiselect;
    },
    toggleImageIntelligence(state, { index }) {
      getNewSurveyQuestions()[index].answer_options.is_image_intelligence = !getNewSurveyQuestions()[index].answer_options.is_image_intelligence;
    },



    duplicateQuestionBlock(state, { index }) {
      let duplicated_question = JSON.parse(JSON.stringify(getNewSurveyQuestions()[index]));
      state.new_survey.questions.splice((index + 1), 0, duplicated_question);
    },

    deleteQuestionBlock(state, { index }) {

      getNewSurveyQuestions().splice(index, 1);

      // Adjust any affected go_to indexes
      let removed_from_index = index;
      _.forEach(state.new_survey.questions, (question, question_index) => {
        if (question.answer_options.go_to > removed_from_index) {
          question.answer_options.go_to--;
        };
        if (question.answer_options.go_to === removed_from_index) {
          question.answer_options.go_to = 'none';
        };

        if (question.answer_type === 'multi_choice') {
          _.forEach(question.multi_choice, (option, option_index) => {
            if (option.go_to === removed_from_index) {
              option.go_to = 'none';
            };
            if (option.go_to > removed_from_index) {
              option.go_to--;
            };
          });
        };
      });

    },

    rearrangeQuestions(state, newQuestions) {

      state.new_survey.questions = newQuestions;

      // TODO: Adjust affected go_to indexes

    },

    setDraftSurvey(state, draft_survey) {
      let question_array = [];
      _.forEach(draft_survey.questions, question => {
        question_array.push(question);
      });
      draft_survey.questions = question_array;
      state.new_survey = draft_survey;
    },

    editSurvey(state, { edited_survey, survey_index }) {
      let copy = JSON.parse(JSON.stringify(state.surveys));
      copy[survey_index] = edited_survey;
      state.surveys = copy;
    },

    decrementWalletBalanceForPush(state, push_count=1) {
      let deduction_amount = push_count * 0.1;
      if (state.billing) {
        state.billing.wallet_balance = state.billing.wallet_balance - deduction_amount;
      };
    },

    resetSurvey(state) {
      state.new_survey = {
        emoji_icon: getRandomEmoji(),
        shortcode: getRandomShortcode(),
        language: "en",
        title: '',
        questions: [],
        starting_message: {
          link: '',
          push: {
            id: '',
            vars: [],
            image: '',
            type: ''
          }
        },
      }
    },

    setLoading(state, { loading_key, loading_value }) {
      state.loading_data[loading_key] = loading_value;
    },





    /********

    SURVEY CARD MUTATIONS

    *******/

    duplicateSurvey(state, duplicated_survey) {
      state.surveys.unshift(duplicated_survey);
    },

    publishSurvey(state, survey_index) {
      state.surveys[survey_index].is_published = true;
      state.surveys[survey_index].link_click_count = 0;
      state.surveys[survey_index].started_users = [];
      state.surveys[survey_index].completed_users = [];
    },

    toggleSurveyPaused(state, { survey_index, is_paused }) {
      state.surveys[survey_index].is_paused = is_paused;
    },

    resetSurveyAnswers(state, { survey_index, reset_survey }) {
      let copy = JSON.parse(JSON.stringify(state.surveys));
      copy[survey_index] = reset_survey;
      state.surveys = copy;
    },

    deleteSurvey(state, deleted_survey_id) {
      state.surveys = _.filter(state.surveys, survey => survey && survey.id !== deleted_survey_id);
    },



    /********

    ACCOUNT SETTINGS

    *******/

    updateTeamName(state, name) {
      state.account.name = name;
    },

    updateTeamMemberRole(state, data) {
      let { member_uid, role } = data;
      let member_index = _.findIndex(state.team_members, ['uid', member_uid]);
      state.team_members[member_index].role = role;
    },

    addTeamMemberInvite(state, email) {
      state.team_members.push({
        email,
        role: 'invited'
      });
    },

    updateTeamMemberInviteId(state, invite_id) {
      state.team_members[state.team_members.length-1].id = invite_id;
    },

    cancelTeamMemberInvite(state, invite_id) {
      let invited_member_index = _.findIndex(state.team_members, ['invite_id', invite_id]);
      state.team_members.splice(invited_member_index, 1);
    },

    removeTeamMember(state, member_index) {
      state.team_members.splice(member_index, 1);
    },

    setBilling(state, data) {
      state.billing = data;
    },

    setAccountPrivate(state, data) {
      state.account.private = data;
    },

    setAccountAPIKey(state, new_api_key) {
      if (state.account.private) {
        let copy = JSON.parse(JSON.stringify(state.account));
        copy.private.api_key = new_api_key;
        state.account = copy;
      } else {
        state.account.private = { api_key: new_api_key };
      };
    },

    setAccountWebhook(state, webhook) {
      if (state.account.private) {
        let copy = JSON.parse(JSON.stringify(state.account));
        copy.private.webhook = webhook;
        state.account = copy;
      } else {
        state.account.private = { webhook };
      };
    },

    toggleAnswerDetailsShown(state, index) {
      state.billing.monthly_usage[index].is_answer_details_shown = !state.billing.monthly_usage[index].is_answer_details_shown;
    }

  },



  actions: {

    initUser({ commit }, uid) {

      // Get the user's account, and load their surveys
      Users.doc(uid).get().then(doc => {

        if (doc.exists) {

          let user = doc.data();
          user.uid = uid;
          commit('initUser', user);

          let account_id = user.account_id;

          if (account_id) { // Happens when originally setting anon user account

            // Get the user's account
            Accounts.doc(account_id).get().then(doc => {

              if (doc.exists) {

                // Load the accounts surveys
                let account = doc.data();
                account.id = account_id;
                commit('initAccount', account);

                // TODO: Could speed this up by making it a single call to collection assuming there is an attribute I can filter on (like account_id) - requires me to set that on surveys, but makes surveys 1-to-1.
                let account_studies = _.reverse(account.studies);

                let survey_promises = _.map(account_studies, (survey_id, survey_index) => getSurvey(survey_id, survey_index));
                Promise.all(survey_promises).then(async surveys => {

                  // Manually uncache answer data. TEMP?
                  surveys = _.map(surveys, survey => {
                    let _survey = survey;
                    if (_survey) {
                      _survey.answers = null;
                      _survey.recent_answers = null;
                    };
                    return _survey;
                  });

                  commit('initSurveys', surveys);

                  // Fetch answers and recent answers for the newest X surveys
                  // if (surveys && surveys.length >= 1) {
                  //   this.dispatch('setAnswers', 0);
                  //   if (surveys.length >= 2) {
                  //     this.dispatch('setAnswers', 1);
                  //   };
                  // };

                  // Set team members (to use in account settings and comments)
                  this.dispatch('setTeamMembers', account_id);

                  // Set billing to display wallet balance in the header - and determine whether a client can send a push
                  this.dispatch('setBilling');

                  // Set API Key
                  this.dispatch('setAccountPrivate');


                });

              };

            });
          }

        };
      });
    },


    // QUESTIONS Page : Set respondents and recent answers
    async setRecentAnswers({ commit, state }, survey_index) {

      let survey_id = getSurveyId(survey_index);

      // Return is recent answers are already set for the study.
      if (this.state.surveys[survey_index] && this.state.surveys[survey_index].recent_answers) {
        return;
      };

      // Set survey respondents
      let survey_respondents = await getSurveyRespondents(survey_id);
      commit('setSurveyRespondents', survey_respondents);

      let recent_answers = await getSurveyRecentAnswers(survey_id); // Get 150 most recent answers
      recent_answers = _.chain(recent_answers).map(answer => {
        let respondent = _.find(this.state.respondents, ['uid', answer.uid]);
        if (!respondent) {
          return null;
        } else {
          answer.profile_pic = respondent.profile_pic;
          answer.name = respondent.first_name || respondent.name || '';
          return answer;
        };
      }).compact().uniqBy('uid').orderBy('time_created', 'desc').slice(0, 10).value();

      commit('setRecentAnswers', {
        survey_index,
        recent_answers
      });

      this.dispatch('setNPSScoreForRespondents', { survey_index });

    },


    // SHARE page : Set all whatsapp pushes for a given study
    async setWhatsappPushes({ commit, state }, { survey_index, get_live_data }) {

      let survey_id = getSurveyId(survey_index);

      // Data already fetched, no need to fetch twice
      if (!get_live_data && this.state.fetched_data[`${survey_id}-pushes`]) {
        return;
      };

      // Set survey respondents (needed to enrich whatsapp push data)
      let survey_respondents = await getSurveyRespondents(survey_id);
      commit('setSurveyRespondents', survey_respondents);

      let whatsapp_pushes = await getSurveyWhatsappPushes(survey_id);

      whatsapp_pushes = _.map(whatsapp_pushes, push => {
        push.time = _.max([push.time_queued, push.time_sent, push.time_read, push.time_survey_opted_out, push.time_survey_started, push.time_survey_completed]);
        if (push.respondent_id) {
          let respondent = _.find(survey_respondents, ['uid', push.respondent_id]);
          if (respondent) {
            push.name = respondent.name;
            if (respondent.completed_studies && respondent.completed_studies.includes(survey_id)) {
              push.status = 'survey_completed';
              push.time_survey_completed = push.time_survey_started + 1; // TODO: Save this on the whatsapp_push doc
            };
            if (respondent.state && respondent.state.study_id === survey_id) {
              push.progress_step = respondent.state.step;
            };
            if (respondent.opt_outs && (respondent.opt_outs.includes(survey_id) || respondent.opt_outs.includes('all'))) {
              push.status = 'survey_opted_out';
            };
          };
        };
        return push;
      });

      // Set whatsapp pushes
      commit('setWhatsappPushes', {
        survey_index,
        whatsapp_pushes
      });

      commit('setFetched', `${survey_id}-pushes`);

      setPushListener(survey_id, survey_index);

    },


    // ANSWERS page : Set all answers for a given study and question index
    async setAnswers({ commit, state }, { survey_index, question_index }) {

      let survey_id = getSurveyId(survey_index);

      // Data already fetched, no need to fetch twice
      if (this.state.fetched_data[`${survey_id}-${question_index}`]) {
        return;
      };

      let survey_answers = await getSurveyAnswersforQuestionIndex(survey_id, parseInt(question_index));

      // Set survey answers
      survey_answers = _.orderBy(survey_answers, ['time_created'], ['desc']); // Order answers by recency
      commit('setAnswers', {
        survey_index,
        answers: survey_answers
      });

      commit('setFetched', `${survey_id}-${question_index}`);

    },


    // RESPONDENT page : Set all answers for a given study and respondent
    async setAnswersForRespondent({ commit, state }, { survey_index, uid }) {

      let survey_id = getSurveyId(survey_index);

      // Data already fetched, no need to fetch twice
      if (this.state.fetched_data[`${survey_id}-${uid}`]) {
        return;
      };

      // Get and set all respondents from the survey
      let respondents_answers = await getSurveyAnswersforRespondent(survey_id, uid)

      // Set survey answers
      respondents_answers = _.orderBy(respondents_answers, ['time_created'], ['desc']); // Order answers by recency
      commit('setAnswers', {
        survey_index,
        answers: respondents_answers
      });
      commit('setFetched', `${survey_id}-${uid}`);

    },

    async setNPSScoreForRespondents({ commit, state }, { survey_index }) {

      let nps_question_index = -1;
      _.forEach(this.state.surveys[survey_index].questions, (question, question_index) => {
        if (question.answer_type === 'nps') {
          nps_question_index = question_index;
        };
      });

      if (nps_question_index === -1) { return };

      let survey_id = getSurveyId(survey_index);
      let nps_answers = await getSurveyAnswersforQuestionIndex(survey_id, parseInt(nps_question_index));

      for (let answer of nps_answers) {
        commit('setRespondentNPSScore', {
          uid: answer.uid,
          nps_score: answer.answer
        });
      };

    },



    // For flagging and removing just one of the respondent's answers
    async deleteAnswer({ commit, state }, data) {

      let [survey_index, question_index, answer_id] = data;

      commit('deleteAnswer', [survey_index, question_index, answer_id]);

      let survey_id = getSurveyId(survey_index);

      // Update the answer count
      var update = {};
      update[`questions.${question_index}.answer_count`] = fieldValue.increment(-1);

      // TODO: If only one question in the survey, also decrement the started_users by the user.
      await Promise.all([
        Surveys.doc(survey_id).collection('answers').doc(answer_id).delete(),
        Surveys.doc(survey_id).update(update)
      ]);

      return;

    },



    // For flagging and removing ALL a respondent's answers
    async deleteAllRespondentsAnswers({ commit, state }, data) {

      let [respondent_id, survey_index] = data;
      let survey_id = getSurveyId(survey_index);

      // Get all the respondents answers for the survey
      let answers = _.filter(this.state.surveys[survey_index].answers, ['uid', respondent_id])

      // Delete all the respondents answers from the survey
      for (let answer of answers) {

        let answer_id = answer.id;
        let question_index = answer.question_index;

        // Mark the answer as "is_deleted"
        Surveys.doc(survey_id).collection('answers').doc(answer_id).delete();

        commit('deleteAnswer', [survey_index, question_index, answer_id, respondent_id]);

      };

      commit('deleteRecentAnswer', [survey_index, respondent_id]);

      // Decrement the answer count of the question
      let uniq_question_indexes = _.chain(answers).uniqBy('question_index').map('question_index').value();
      // TODO: Double check that this logic is correct.
      for (let question_index of uniq_question_indexes) {
        var update = {};
        update[`questions.${question_index}.answer_count`] = fieldValue.increment(-1);
        await Surveys.doc(survey_id).update(update);
      };

      // Add the survey to the respondent's list of ejected surveys and vice-versa
      Respondents.doc(respondent_id).update({ ejected_studies: fieldValue.arrayUnion(survey_id) });
      Surveys.doc(survey_id).update({ ejected_users: fieldValue.arrayUnion(respondent_id) });

      // Remove the survey from the respondent's list of completed surveys and vice-versa
      Respondents.doc(respondent_id).update({ completed_studies: fieldValue.arrayRemove(survey_id) });
      Surveys.doc(survey_id).update({ completed_users: fieldValue.arrayRemove(respondent_id) });

      // Remove the survey from the respondent's list of started surveys and vice-versa
      Respondents.doc(respondent_id).update({ started_studies: fieldValue.arrayRemove(survey_id) });
      Surveys.doc(survey_id).update({ started_users: fieldValue.arrayRemove(respondent_id) });

    },


    editSurvey({ commit, state }, data) {
      let [survey_index, survey, edits] = data;
      let survey_id = getSurveyId(survey_index);
      Surveys.doc(survey_id).update(edits);
      let edited_survey = survey;
      edited_survey.survey_index = survey_index;
      edited_survey.survey_id = survey_id;
      commit('editSurvey', { survey_index, edited_survey });
    },


    // Let the user preview the survey by sending it to themselves on WhatsApp directly
    sendSurveyToWhatsapp({ commit, state }, { pushes, survey_index }) {
      let client_id = this.state.user.uid;
      let survey_id = getSurveyId(survey_index);
      axios.post(`https://api-outsidevoice.vercel.app/queue-whatsapp-pushes`, {
        pushes,
        survey_id,
        client_id
      }).then(res => {
        let { push_id } = res.data
        this.dispatch('sendWhatsappPush', { survey_index, push_id });
      }).catch(err => {
        console.log(err);
      });
    },


    // Send a particular whatsapp push
    sendWhatsappPush({ commit, state }, { survey_index, push_id }) {

      // Set push as loading so that the UI updates to the loading state
      commit('setWhatsappPushStatusSending', { survey_index, push_id });

      // Update wallet balance
      commit('decrementWalletBalanceForPush');

      let survey_id = getSurveyId(survey_index);
      let client_id = this.state.user.uid;
      axios.post(`https://api-outsidevoice.vercel.app/send-whatsapp-push`, {
        survey_id,
        push_id,
        client_id
      }).then(res => {
        console.log('https://api-outsidevoice.vercel.app/send-whatsapp-push returned');
      }).catch(err => {
        console.log(err);
      });
    },

    // Create pending whatsapp pushes from an array of numbers
    queueWhatsappPushes({ commit, state }, { pushes, survey_index }) {
      let survey_id = getSurveyId(survey_index);
      let client_id = this.state.user.uid;
      axios.post(`https://api-outsidevoice.vercel.app/queue-whatsapp-pushes`, {
        pushes,
        survey_id,
        client_id
      }).then(res => {
        console.log('https://api-outsidevoice.vercel.app/queue-whatsapp-push returned');
        this.dispatch('setWhatsappPushes', { survey_index, get_live_data: true });
      }).catch(err => {
        console.log(err);
      });
    },

    unqueueWhatsappPushes({ commit, state }, { survey_index, push_ids }) {
      commit('removeWhatsappPushes', { survey_index, push_ids }); // Optimistically remove push
      let survey_id = getSurveyId(survey_index);
      let client_id = this.state.user.uid;
      axios.post(`https://api-outsidevoice.vercel.app/unqueue-whatsapp-pushes`, {
        survey_id,
        push_ids,
        client_id
      }).then(res => {
        console.log('https://api-outsidevoice.vercel.app/unqueue-whatsapp-pushes returned');
      }).catch(err => {
        console.log(err);
      });

    },

    // Create pending whatsapp pushes from an array of numbers
    sendQueuedWhatsappPushes({ commit, state }, { survey_index, push_count }) {

      // Update wallet balance
      commit('decrementWalletBalanceForPush', push_count);

      // Call API to send all queued pushes for the given survey
      let survey_id = getSurveyId(survey_index);
      let client_id = this.state.user.uid;
      axios.post(`https://api-outsidevoice.vercel.app/send-queued-whatsapp-pushes`, {
        survey_id,
        client_id
      }).then(res => {
        console.log('https://api-outsidevoice.vercel.app/send-queued-whatsapp-pushes returned');
        commit('setLoading', { loading_key: 'is_sending_queued_pushes', loading_value: false });
        this.dispatch('setWhatsappPushes', { survey_index, get_live_data: true });
      }).catch(err => {
        console.log(err);
      });

    },


    publishSurvey({ commit, state }, survey_index) {
      let survey_id = getSurveyId(survey_index);
      Surveys.doc(survey_id).update({ is_published: true });
      commit('publishSurvey', survey_index);
    },

    toggleSurveyPaused({ commit, state }, { survey_index, is_paused }) {
      let survey_id = getSurveyId(survey_index);
      Surveys.doc(survey_id).update({ is_paused });
      commit('toggleSurveyPaused', { survey_index, is_paused });
    },

    resetSurveyAnswers({ commit, state }, survey_index) {
      let survey_id = getSurveyId(survey_index);
      let client_id = this.state.user.uid;
      axios.delete(`https://api-outsidevoice.vercel.app/reset-study-answers?study_id=${survey_id}&client_id=${client_id}`).then((response) => {
        let data = response.data;
        if (data.reset_survey) {
          let reset_survey = data.reset_survey;
          reset_survey.index = survey_index;
          reset_survey.id = survey_id;
          reset_survey.answers = [];
          reset_survey.recent_answers = [];
          commit('resetSurveyAnswers', { survey_index, reset_survey });
        };
      }).catch(err => {
        console.log(err);
      });
    },

    duplicateSurvey({ commit, state }, survey_id) {
      let client_id = this.state.user.uid;
      axios.put(`https://api-outsidevoice.vercel.app/duplicate-survey`, { survey_id, client_id }).then((response) => {
        let data = response.data;
        let duplicated_survey = data.duplicated_survey;
        console.log({ duplicated_survey });
        if (duplicated_survey) {
          commit('duplicateSurvey', duplicated_survey);
          commit('updateSurveyIndexes');
          this.dispatch('setNewSurveyRealtimeAnswerListener');
        };
      }).catch(err => {
        console.log({ err });
      });

    },


    deleteSurvey({ commit, state }, survey_id) {
      let client_id = this.state.user.uid;
      axios.delete(`https://api-outsidevoice.vercel.app/delete-study?study_id=${survey_id}&client_id=${client_id}`).catch(err => {
        console.log(err);
      });
      commit('deleteSurvey', survey_id);
      commit('updateSurveyIndexes');
    },


    // For flagging and removing just one of the respondent's answers (for closed surveys)
    async addComment({ commit, state }, data) {
      let [survey_index, comment_text] = data;
      let survey_id = getSurveyId(survey_index);
      let answer_id = this.state.comments_answer_id;
      let author = this.state.user.name || '';
      let comment = {
        text: comment_text,
        author,
        time_created: moment().unix()
      };
      commit('addComment', { survey_index, comment });
      Surveys.doc(survey_id).collection('answers').doc(answer_id).update({
        comments: fieldValue.arrayUnion(comment)
      });
    },

    toggleIsFavorited({ commit, state }, data) {
      let { survey_index, answer_id, is_favorited } = data;
      let survey_id = getSurveyId(survey_index);
      commit('toggleIsFavorited', { survey_index, answer_id, is_favorited });
      Surveys.doc(survey_id).collection('answers').doc(answer_id).update({ is_favorited });
    },

    editAnswerTranscript({ commit, state }, data) {
      let { survey_index, answer_id, edited_transcript } = data;
      let survey_id = getSurveyId(survey_index);
      commit('editAnswerTranscript', { survey_index, answer_id, edited_transcript });
      Surveys.doc(survey_id).collection('answers').doc(answer_id).update({ transcription: edited_transcript });
    },

    setNewSurveyRealtimeAnswerListener({ commit, state }) {
      let survey_id = getSurveyId(0);
      setSurveyListener(survey_id, 0);
      // TODO, reset all the other survey listeners and their indexes too... Otherwise adding a new survey messes things up.
    },


    // SETTINGS

    // GENERAL page
    updateUser({ commit, state }, data) {
      let user_id = this.state.user.uid;
      Users.doc(user_id).update(data);
      commit('updateUser', data);
    },

    // TEAM page
    updateTeamName({ commit, state }, name) {
      let account_id = this.state.user.account_id;
      commit('updateTeamName', name);
      Accounts.doc(account_id).update({ name });
    },

    async setTeamMembers({ commit, state }, account_id) {
      let team_members = await getTeamMembers(account_id);
      if (this.state.user.role === 'admin') {
        let invited_team_members = await getInvitedTeamMembers(account_id);
        team_members = team_members.concat(invited_team_members);
      };
      commit('initTeamMembers', team_members);
    },

    updateTeamMemberRole({ commit, state }, data) {
      let { member_uid, role } = data;
      Users.doc(member_uid).update({ role });
      commit('updateTeamMemberRole', { member_uid, role });
      if (member_uid === this.state.user.uid) {
        commit('updateUser', { role });
      };
    },

    async removeTeamMember({ commit, state }, { member_index, member }) {
      commit('removeTeamMember', member_index);
      try {
        let uid = this.state.user.uid;
        let uid_to_remove = member.uid;
        await axios.put(`https://api-outsidevoice.vercel.app/remove-user-from-account`, { uid, uid_to_remove });
      } catch (err) {
        console.log(err);
        return;
      };
    },

    inviteTeamMember({ commit, state }, invite) {
      commit('addTeamMemberInvite', invite.email);
      let user = this.state.user;
      let invite_data = {
        invited_email: invite.email,
        role: invite.role,
        team_name: this.state.account.name || '',
        inviter_name: user.name || '',
        inviter_email: user.email || '',
        inviter_profile_pic: user.profile_pic || '',
        account_id: user.account_id,
        time_created: moment().unix()
      };
      Misc.doc('teammate_invites').collection('invites').add(invite_data).then(function(doc) {
        let invite_id = doc.id
        commit('updateTeamMemberInviteId', invite_id);
      }).catch(function(err) {
        console.log('Err adding invite', err);
      });
    },

    cancelTeamMemberInvite({ commit, state }, invite_id) {
      commit('cancelTeamMemberInvite', invite_id);
      Misc.doc('teammate_invites').collection('invites').doc(invite_id).delete();
    },

    // BILLING page
    setBilling({ commit, state }) {
      let user = this.state.user;
      axios.get(`https://api-outsidevoice.vercel.app/get-billing?client_id=${user.uid}`).then(res => {

        let data = res.data;
        if (data.data) {

          // Set state so that all answer type details are hidden
          for (let i = 0; i < data.data.monthly_usage.length; i++) {
            data.data.monthly_usage[i].is_answer_details_shown = false;
          };

          // Set the title of surveys, group deleted survey cost.
          let deleted_survey_usage_total = 0;
          for (let i = 0; i < data.data.survey_usage.length; i++) {
            let matching_survey = _.find(this.state.surveys, ['id', data.data.survey_usage[i].survey_id])
            if (matching_survey) {
              data.data.survey_usage[i].title = matching_survey.title;
            } else {
              deleted_survey_usage_total += data.data.survey_usage[i].usage
            };
          };

          // Remove any deleted surveys
          data.data.survey_usage = _.filter(data.data.survey_usage, function(survey) { return survey.title; });

          data.data.survey_usage.push({
            id: null,
            title: 'Deleted Surveys',
            usage: deleted_survey_usage_total
          });

          //console.log(data.data);
          commit('setBilling', data.data);
        };
      }).catch(err => {
        console.log(err);
      });
    },

    // API page
    setAccountPrivate({ commit, state }) {
      let user = this.state.user;
      Accounts.doc(user.account_id).collection('private').doc('data').get().then(doc => {
        if (doc.exists) {
          commit('setAccountPrivate', doc.data());
        };
      }).catch(err => {
        console.log('Error getting account private', err);
      });
    },

    generateNewAPIKey({ commit, state }) {
      const new_api_key = db_helper.getRandomAPIKey();
      let user = this.state.user;
      Accounts.doc(user.account_id).collection('private').doc('data').update({ api_key: new_api_key }).then(() => {
        commit('setAccountAPIKey', new_api_key);
      }).catch(function(err) {
        console.log('Err generating new api key', err);
      });
    },

    editWebhook({ commit, state }, webhook) {
      let user = this.state.user;
      Accounts.doc(user.account_id).collection('private').doc('data').update({ webhook }).then(() => {
        commit('setAccountWebhook', webhook);
      }).catch(function(err) {
        console.log('Err setting webhook', err);
      });
    },

  },

  getters: {}

});


function getSurvey(survey_id, index) {
  return new Promise(async (resolve, reject) => {
    try {
      Surveys.doc(survey_id).get().then(doc => {
        if (doc.exists) {
          let survey_data = doc.data();
          survey_data.index = index;
          survey_data.id = survey_id;
          resolve(survey_data);
        } else {
          resolve();
        };
      }).catch(err => {
        resolve();
      });
    } catch (err) {
      console.log('Err getting survey', err);
      resolve();
    };
  });
};


// For Answers Page
function getSurveyWhatsappPushes(survey_id) {
  return new Promise(async (resolve, reject) => {
    Surveys.doc(survey_id).collection('whatsapp_pushes').get().then(function(snapshot) {
      let whatsapp_pushes = [];
      snapshot.forEach(function(doc) {
        let data = doc.data();
        data.id = doc.id;
        whatsapp_pushes.push(data);
      });
      resolve(whatsapp_pushes);
    }).catch(err => {
      console.log('Error getting survey whatsapp pushes', err);
      resolve([]);
    });
  });
};


// For Questions Page
function getSurveyRecentAnswers(survey_id) {
  return new Promise(async (resolve, reject) => {
    Surveys.doc(survey_id).collection('answers').orderBy('time_created', 'desc').limit(200).get().then(function(snapshot) {
      let survey_answers = [];
      snapshot.forEach(function(doc) {
        let data = doc.data();
        data.id = doc.id;
        survey_answers.push(data);
      });
      resolve(survey_answers);
    }).catch(err => {
      console.log('Error getting recent survey answers', err);
      resolve([]);
    });
  });
};


// For Answers Page
function getSurveyAnswersforQuestionIndex(survey_id, question_index) {
  return new Promise(async (resolve, reject) => {
    Surveys.doc(survey_id).collection('answers').where('question_index', '==', question_index).get().then(function(snapshot) {
      let survey_answers = [];
      snapshot.forEach(function(doc) {
        let data = doc.data();
        data.id = doc.id;
        survey_answers.push(data);
      });
      resolve(survey_answers);
    }).catch(err => {
      console.log('Error getting survey answers', err);
      resolve([]);
    });
  });
};


// For Respondent Page
function getSurveyAnswersforRespondent(survey_id, respondent_uid) {
  return new Promise(async (resolve, reject) => {
    Surveys.doc(survey_id).collection('answers').where('uid', '==', respondent_uid).get().then(function(snapshot) {
      let survey_answers = [];
      snapshot.forEach(function(doc) {
        let data = doc.data();
        data.id = doc.id;
        survey_answers.push(data);
      });
      resolve(survey_answers);
    }).catch(err => {
      console.log('Error getting survey answers for respondent', err);
      resolve([]);
    });
  });
};



function getSurveyRespondents(survey_id) {
  return new Promise(async (resolve, reject) => {
    try {
      Respondents.where("started_studies", "array-contains", survey_id).get().then(snapshot => {
        let results = [];
        if (!snapshot.empty) {
          snapshot.forEach(doc => {
            results.push(formatRespondent(doc));
          });
        };
        resolve(results);
      });
    } catch (err) {
      console.log('Err getting respondents for survey', err);
      resolve([]);
    };
  });
};


function getRespondent(uid) {
  return new Promise(async (resolve, reject) => {
    try {
      Respondents.doc(uid).get().then(doc => {
        if (doc.exists) {
          resolve(formatRespondent(doc));
        } else {
          resolve();
        };
      }).catch(err => {
        resolve();
      });
    } catch (err) {
      console.log('Err getting respondent', uid, err);
      resolve();
    };
  });
};


function formatRespondent(doc) {
  let respondent = doc.data();
  respondent.id = doc.id;
  respondent.uid = doc.id;

  let profile_pic_character = '';
  if (respondent.platform === 'messenger') {
    profile_pic_character = ['a','b','c','d','e','f','g','h','i','j'][(respondent.uid).charAt(0)];
    //console.log('profile_pic_character', profile_pic_character);
  } else {
    let uid_without_numbers = (respondent.uid).replace(/[0-9]/g, '');
    //console.log(uid_without_numbers, uid_without_numbers)
    //console.log('uid_without_numbers.charAt(0)', uid_without_numbers.charAt(0));
    profile_pic_character = uid_without_numbers.charAt(0);
  };
  const alphabet = ['a','b','c','d','e','f','g','h','i','j','k','l','m','n','o','p','q','r','s','t','u','v','w','x','y','z'];
  respondent.profile_pic = `https://api-outsidevoice.vercel.app/public/respondent-bg-${profile_pic_character}.jpg`;
  return respondent;
};


// Realtime Answer Listeners
let fetched_surveys = [];
function setSurveyListener(survey_id, survey_index) {
  Surveys.doc(survey_id).collection('answers').onSnapshot(snapshot => {
    if (fetched_surveys.includes(survey_id)) { // Don't set answers when first initing answers
      snapshot.docChanges().forEach(async change => {
        let answer = change.doc.data();
        answer.id = change.doc.id;

        if (change.type === "added") {

          store.commit('addNewAnswer', { survey_index, answer });

          // Add respondent and recent answer
          let respondent = store.state.respondents[answer.uid];
          if (!respondent) {
            respondent = await getRespondent(answer.uid);
            store.commit('setRespondent', respondent);
          };

          let recent_answer_index = _.findIndex(store.state.surveys[survey_index].recent_answers, ['uid', answer.uid])
          if (recent_answer_index === -1) {
            let recent_answer = answer;
            recent_answer.profile_pic = respondent.profile_pic;
            recent_answer.name = respondent.first_name || respondent.name || '';
            store.commit('addNewRecentAnswer', { survey_index, recent_answer });
          };

        };
        if (change.type === "modified") {
          store.commit('updateNewAnswer', { survey_index, answer });
        };
      });

    } else {
      fetched_surveys.push(survey_id);
    };

  });
};


// Realtime whatsapp_pushes Listeners
let fetched_survey_pushes = [];
function setPushListener(survey_id, survey_index) {

  Surveys.doc(survey_id).collection('whatsapp_pushes').onSnapshot(snapshot => {

    if (fetched_survey_pushes.includes(survey_id)) { // Don't set pushes when first initing whatsapp_pushes

      snapshot.docChanges().forEach(async change => {

        let push = change.doc.data();

        push.id = change.doc.id;
        push.time = _.max([push.time_queued, push.time_sent, push.time_read, push.time_survey_opted_out, push.time_survey_started, push.time_survey_completed]);

        if (change.type === "modified") {
          store.commit('updateWhatsappPush', { survey_index, push });
        };

      });

    } else {
      fetched_survey_pushes.push(survey_id);
    };

  });
};


function getTeamMembers(account_id) {
  return new Promise(async (resolve, reject) => {
    try {
      Users.where("account_id", "==", account_id).get().then(snapshot => {
        let team_members = [];
        if (!snapshot.empty) {
          snapshot.forEach(doc => {
            team_members.push(doc.data());
          });
        };
        resolve(team_members);
      });
    } catch (err) {
      console.log('Err getting team members for account id', account_id, err);
      resolve([]);
    };
  });
};


function getInvitedTeamMembers(account_id) {
  return new Promise(async (resolve, reject) => {
    try {
      Misc.doc('teammate_invites').collection('invites').where("account_id", "==", account_id).get().then(snapshot => {
        let invited_team_members = [];
        if (!snapshot.empty) {
          snapshot.forEach(doc => {
            let data = doc.data();
            invited_team_members.push({
              email: data.invited_email,
              role: 'invited',
              invite_id: doc.id
            });
          });
        };
        resolve(invited_team_members);
      });
    } catch (err) {
      console.log('Err getting invited team members for account id', account_id, err);
      resolve([]);
    };
  });
};


// Helper Functions
function getNewSurveyQuestions() {
  return store.state.new_survey.questions;
}

function getSurveyId(survey_index) {
  if (store.state.surveys) {
    return store.state.surveys[survey_index].id;
  } else {
    console.log('Could not find survey id for survey at index: ', survey_index);
    return undefined;
  }
};

function getRandomEmoji() {
  return _.shuffle(["✌","😂","😝","😁","😱","👉","🙌","🍻","🔥","🌈","📊","📈","👪","👨‍👩‍👧","👨‍👩‍👧‍👦","💼","👓","🕶","🌂","☂️","☀","🐖","🌴","🌱","🌿","☘️","🍀","🎍","🎋","🍃","🍂","🍁","🍄","🍸","🍹", "🍾","🐐","🐏","🐑","🐕","🐩","🐈","🐓","🦃","🕊","🐇","🎈","🌹","💄","🎀","⚽","🎾","🏁","😡","👿","🐻","🐶","🐬","🐟","🍀","👀","🚗","🍎","💝","💙","👌","❤","😍","😉","😓","😳","💪","💩","🍸","🔑","💖","🌟","🎉","🌺","🎶","👠","🏈","⚾","🏆","👽","💀","🐵","🐮","🐩","🐎","💣","👃","👂","🍓","💘","💜","👊","💋","😘","😜","😵","🙏","👋","🚽","💃","💎","🚀","🌙","🎁","⛄","🚗", "🚕", "🚙", "🚌", "🚎", "🏎", "🚓","🚧", "⛽️", "🚏", "🚦","🌊","⛵","🏀","🎱","💰","👶","🛠", "⛏", "🔩", "⚙️", "⛓", "🔫","👸","🐰","🐷","🐍","🐫","🔫","👄","🚲","🍉","💛","💚"])[0];
};

function getRandomShortcode() {
  const length = 4;
  const possible = "abcdefghijklmnopqrstuvwxyz0123456789";
  var text = "";
  for (var i = 0; i < length; i++) {
    text += possible.charAt(Math.floor(Math.random() * possible.length));
  };
  return text;
}
